import canvasConfetti from "canvas-confetti";

export type CelebrationKind = "quest" | "achievement";

export type CelebrationDetail = {
  kind?: CelebrationKind | string;
  particleCount?: number;
  spread?: number;
  startVelocity?: number;
  decay?: number;
  scalar?: number;
  ticks?: number;
  angle?: number;
  drift?: number;
  origin?: { x?: number; y?: number };
  colors?: string[];
  disableForReducedMotion?: boolean;
};

type ConfettiLauncher = (options: Record<string, unknown>) => Promise<unknown> | unknown;

export type WMUConfettiAPI = {
  burst: (kind?: CelebrationKind | string, detail?: CelebrationDetail) => boolean;
  fire: (detail?: CelebrationDetail) => boolean;
  quest: (detail?: CelebrationDetail) => boolean;
  achievement: (detail?: CelebrationDetail) => boolean;
};

type WMUConfettiWindow = Window & {
  WMUConfetti?: WMUConfettiAPI;
  __WMU_CONFETTI_BOOTED?: boolean;
};

const overlayClassName = "wm-confetti-canvas";
const hideDelayMs = 2800;

const palettes: Record<CelebrationKind, string[]> = {
  quest: ["#4FC3F7", "#22C55E", "#FFCE72", "#9B8AFB"],
  achievement: ["#FFCE72", "#4FC3F7", "#22C55E", "#9B8AFB", "#FF7A59"],
};

const presets: Record<CelebrationKind, Omit<CelebrationDetail, "kind">> = {
  quest: {
    particleCount: 88,
    spread: 74,
    startVelocity: 34,
    decay: 0.92,
    scalar: 0.95,
    ticks: 220,
    angle: 90,
    drift: 0,
    origin: { x: 0.5, y: 0.62 },
    colors: palettes.quest,
    disableForReducedMotion: true,
  },
  achievement: {
    particleCount: 128,
    spread: 88,
    startVelocity: 42,
    decay: 0.93,
    scalar: 1.05,
    ticks: 260,
    angle: 90,
    drift: 0,
    origin: { x: 0.5, y: 0.58 },
    colors: palettes.achievement,
    disableForReducedMotion: true,
  },
};

let launcher: ConfettiLauncher | null = null;
let canvasEl: HTMLCanvasElement | null = null;
let hideTimer = 0;

function prefersReducedMotion(): boolean {
  return Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
}

function normalizeKind(raw: CelebrationKind | string | undefined): CelebrationKind {
  const value = String(raw || "").trim().toLowerCase();
  if (value === "quest" || value === "quest_complete") return "quest";
  return "achievement";
}

function ensureCanvas(): HTMLCanvasElement {
  if (canvasEl instanceof HTMLCanvasElement && canvasEl.isConnected) {
    return canvasEl;
  }
  const existing = document.querySelector(`canvas.${overlayClassName}`);
  if (existing instanceof HTMLCanvasElement) {
    canvasEl = existing;
    return canvasEl;
  }
  canvasEl = document.createElement("canvas");
  canvasEl.className = overlayClassName;
  canvasEl.setAttribute("aria-hidden", "true");
  canvasEl.setAttribute("role", "presentation");
  Object.assign(canvasEl.style, {
    position: "fixed",
    inset: "0",
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    zIndex: "3900",
    visibility: "hidden",
  });
  document.body.appendChild(canvasEl);
  return canvasEl;
}

function ensureLauncher(): ConfettiLauncher {
  if (launcher) {
    return launcher;
  }
  launcher = canvasConfetti.create(ensureCanvas(), { resize: true, useWorker: true }) as ConfettiLauncher;
  return launcher;
}

function normalizeDetail(detail: CelebrationDetail = {}): CelebrationDetail {
  const kind = normalizeKind(detail.kind);
  const preset = presets[kind];
  const origin = detail.origin && typeof detail.origin === "object"
    ? {
        x: Number.isFinite(Number(detail.origin.x)) ? Number(detail.origin.x) : preset.origin?.x,
        y: Number.isFinite(Number(detail.origin.y)) ? Number(detail.origin.y) : preset.origin?.y,
      }
    : preset.origin;
  const colors = Array.isArray(detail.colors)
    ? detail.colors.map((value) => String(value || "").trim()).filter(Boolean)
    : preset.colors;

  return {
    ...preset,
    ...detail,
    kind,
    origin,
    colors: colors && colors.length > 0 ? colors : preset.colors,
  };
}

function showCanvas(): void {
  const canvas = ensureCanvas();
  canvas.style.visibility = "visible";
  if (hideTimer) {
    window.clearTimeout(hideTimer);
  }
  hideTimer = window.setTimeout(() => {
    if (canvasEl instanceof HTMLCanvasElement) {
      canvasEl.style.visibility = "hidden";
    }
  }, hideDelayMs);
}

function fire(detail: CelebrationDetail = {}): boolean {
  const normalized = normalizeDetail(detail);
  if (normalized.disableForReducedMotion !== false && prefersReducedMotion()) {
    return false;
  }
  showCanvas();
  void ensureLauncher()({
    particleCount: normalized.particleCount,
    spread: normalized.spread,
    startVelocity: normalized.startVelocity,
    decay: normalized.decay,
    scalar: normalized.scalar,
    ticks: normalized.ticks,
    angle: normalized.angle,
    drift: normalized.drift,
    origin: normalized.origin,
    colors: normalized.colors,
    disableForReducedMotion: normalized.disableForReducedMotion !== false,
  });
  return true;
}

function burst(kind: CelebrationKind | string = "achievement", detail: CelebrationDetail = {}): boolean {
  return fire({ ...detail, kind });
}

const api: WMUConfettiAPI = {
  burst,
  fire,
  quest: (detail = {}) => burst("quest", detail),
  achievement: (detail = {}) => burst("achievement", detail),
};

function boot(): void {
  document.addEventListener("wmu:celebration", (event) => {
    if (event instanceof CustomEvent && event.detail && typeof event.detail === "object") {
      fire(event.detail as CelebrationDetail);
      return;
    }
    fire();
  });
}

const confettiWindow = window as WMUConfettiWindow;

if (!confettiWindow.__WMU_CONFETTI_BOOTED) {
  confettiWindow.__WMU_CONFETTI_BOOTED = true;
  confettiWindow.WMUConfetti = api;
  boot();
}
